import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagicWandSparkles,
  faTriangleExclamation,
  faCircleCheck,
  faCopy,
} from '@fortawesome/free-solid-svg-icons';

import './DocumentNameSuggestionForm.css';

/**
 * DocumentNameSuggestionForm
 * ===========================
 *
 * The name box in every upload window. Since ClickUp 1248h53bmhy (Marcel,
 * Chris's call 3, 2026-10-29) nothing is read before the upload:
 *   1. Five boxes (date, type, doc number, partner, location) for anyone
 *      who wants to type a name now; they start empty
 *   2. With nothing typed, the document is saved under its original
 *      filename ("Saved as: …")
 *   3. The reader runs in the background AFTER the upload; the suggestion
 *      appears next to the document (SuggestedName) and is taken with one
 *      click. Nothing here waits on it, and the upload is never blocked
 *      for want of a name
 *   4. Calls onChange({ components, displayName, file, userOverrides,
 *      isValid, isLoading, ... }) any time anything changes so the parent
 *      can submit the upload and hand the file to confirmName
 *
 * ``autoRun`` is accepted for the upload windows that still pass it and
 * does nothing: the pre-upload reading it switched on is gone.
 */

const COMPONENT_FIELDS = [
  {
    key: 'component_date',
    label: 'Date',
    placeholder: 'YYYY-MM-DD',
    type: 'text',
    maxLength: 16,
    gridClass: 'doc-name-field--date',
  },
  {
    key: 'component_type_code',
    label: 'Type',
    placeholder: 'e.g. BL',
    type: 'text',
    maxLength: 16,
    transform: (value) => (value || '').toUpperCase(),
    gridClass: 'doc-name-field--type',
  },
  {
    key: 'component_location_flag',
    label: 'Loc',
    placeholder: 'D / F',
    type: 'text',
    maxLength: 4,
    transform: (value) => (value || '').toUpperCase(),
    gridClass: 'doc-name-field--location',
  },
  {
    key: 'component_doc_number',
    label: 'Doc Number',
    placeholder: 'e.g. 12345',
    type: 'text',
    maxLength: 128,
    gridClass: 'doc-name-field--doc-number',
  },
  {
    key: 'component_partner_name',
    label: 'Partner',
    placeholder: 'Partner / counterparty',
    type: 'text',
    maxLength: 256,
    gridClass: 'doc-name-field--partner',
  },
];

const SEPARATOR = ' - ';
const COPIED_FEEDBACK_MS = 1500;

const composeDisplayName = (values) => {
  const parts = [
    values.component_date,
    values.component_type_code,
    values.component_doc_number,
    values.component_partner_name,
    values.component_location_flag,
  ]
    .map((part) => (part || '').trim())
    .filter(Boolean);
  return parts.join(SEPARATOR);
};

const buildInitialValues = (suggestion) => {
  if (!suggestion) {
    return {
      component_date: '',
      component_type_code: '',
      component_doc_number: '',
      component_partner_name: '',
      component_location_flag: '',
    };
  }
  return {
    component_date: suggestion.component_date?.value || '',
    component_type_code: suggestion.component_type_code?.value || '',
    component_doc_number: suggestion.component_doc_number?.value || '',
    component_partner_name: suggestion.component_partner_name?.value || '',
    component_location_flag: suggestion.component_location_flag?.value || '',
  };
};

const buildAiSnapshot = (suggestion) => {
  if (!suggestion) return {};
  return {
    component_date: suggestion.component_date?.value || '',
    component_type_code: suggestion.component_type_code?.value || '',
    component_doc_number: suggestion.component_doc_number?.value || '',
    component_partner_name: suggestion.component_partner_name?.value || '',
    component_location_flag: suggestion.component_location_flag?.value || '',
  };
};

const DocumentNameSuggestionForm = ({
  file,
  module,
  relatedEntityId = '',
  typeDropdownValue = '',
  partnerName = '',
  kycSubtype = '',
  onChange,
  disabled = false,
  autoRun = false, // eslint-disable-line no-unused-vars
}) => {
  const suggestion = null; // nothing is read before the upload any more
  const [values, setValues] = useState(buildInitialValues(null));
  const [overrides, setOverrides] = useState([]);
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef(null);
  const typedName = composeDisplayName(values);
  const savedAs = typedName || file?.name || '';

  useEffect(() => {
    if (!onChange) return;
    onChange({
      components: values,
      displayName: savedAs,
      // The file itself, so the upload window can hand it to confirmName
      // and the reader can run in the background after the upload.
      file: file || null,
      originalFilename: file?.name || '',
      typeDropdownValue,
      partnerName,
      kycSubtype,
      relatedEntityId,
      aiSuggestion: buildAiSnapshot(suggestion),
      userOverrides: overrides,
      extractionConfidence: null,
      hasSuggestion: false,
      // Nothing runs before the upload now, so nothing is ever in flight.
      isLoading: false,
      // A file always has a name to be saved under (what was typed, else
      // its own filename), so the upload is never held up for a name.
      isValid: !!file,
    });
  }, [values, overrides, savedAs, file, typeDropdownValue, partnerName, kycSubtype, relatedEntityId, onChange]);

  useEffect(() => () => {
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
  }, []);

  const handleFieldChange = (field, rawValue) => {
    const transformed = field.transform ? field.transform(rawValue) : rawValue;
    setValues((prev) => ({ ...prev, [field.key]: transformed }));
    setOverrides((prev) => (prev.includes(field.key) ? prev : [...prev, field.key]));
  };

  const handleCopy = useCallback(() => {
    const name = composeDisplayName(values);
    if (!name) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(name).then(
        () => {
          setCopied(true);
          if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
          copyTimerRef.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
        },
        () => {
          setCopied(false);
        },
      );
    }
  }, [values]);

  const previewName = typedName;

  return (
    <div className="doc-name-form">
      <div className="doc-name-form-header">
        <div className="doc-name-form-title">
          <FontAwesomeIcon icon={faMagicWandSparkles} />
          <span>Document name</span>
        </div>
      </div>

      <div className="doc-name-after-save">
        A name suggestion will appear next to the document a few seconds after
        it is saved; take it with one click. Type a name below only if you
        want to set one now.
      </div>

      <div className="doc-name-fields">
        {COMPONENT_FIELDS.map((field) => (
          <div className={`doc-name-field ${field.gridClass}`} key={field.key}>
            <label className="doc-name-label" htmlFor={`doc-name-${field.key}`}>
              <span className="doc-name-label-text">{field.label}</span>
            </label>
            <input
              id={`doc-name-${field.key}`}
              type={field.type}
              value={values[field.key] || ''}
              maxLength={field.maxLength}
              placeholder={field.placeholder}
              onChange={(event) => handleFieldChange(field, event.target.value)}
              disabled={disabled}
              className="doc-name-input"
            />
          </div>
        ))}
      </div>

      <div className="doc-name-preview">
        <span className="doc-name-preview-label">Saved as</span>
        <span className="doc-name-preview-value" title={savedAs}>
          {savedAs || <em>Select a file first</em>}
        </span>
        <button
          type="button"
          className={`doc-name-copy-btn ${copied ? 'doc-name-copy-btn--copied' : ''}`}
          onClick={handleCopy}
          disabled={!previewName}
          title={copied ? 'Copied!' : 'Copy name'}
          aria-label={copied ? 'Copied' : 'Copy typed name'}
        >
          <FontAwesomeIcon icon={copied ? faCircleCheck : faCopy} />
        </button>
      </div>

      {!file && (
        <div className="doc-name-status">
          <FontAwesomeIcon icon={faTriangleExclamation} /> Select a file to upload.
        </div>
      )}
    </div>
  );
};

export default DocumentNameSuggestionForm;
